import { format } from "date-fns";
import * as locales from 'date-fns/locale';
import i18next from "i18next";
import { PricingType } from "../../manualAccommodation/objects/pricingType";
import { CancellationRule } from "../objects/cancellationRule";
import { ClosedDay } from "../objects/closedDay";
import { Column } from "../objects/column";
import { ActivityType, ManualActivity } from "../objects/manualActivity";
import { ManualActivityCancellationRuleListField } from "../objects/manualActivityCancellationRuleListField";
import { ManualActivityContactListField } from "../objects/manualActivityContactListField";
import { ManualActivityContractParameters } from "../objects/manualActivityContractParameters";
import { ManualActivityErrors } from "../objects/manualActivityErrors";
import { ManualActivityListConfiguration } from "../objects/manualActivityListConfiguration";
import { ManualActivityListCriteria } from "../objects/manualActivityListCriteria";
import { ManualActivityListFiltersCriteria } from "../objects/manualActivityListFiltersCriteria";
import { ManualActivityPrice, ManualActivityPriceUtil } from "../objects/manualActivityPrice";
import { OpenDays } from "../objects/openDays";
import { Rule } from "../objects/rule";
import { ManualActivityContact } from './../objects/manualActivityContact';
import { Place } from "../../place/objects/place";
import { ManualActivityLocalized } from "../objects/manualActivityLocalized";

const locales_cpy: { [key: string]: any } = locales;

export const MANUAL_ACTIVITY_MODULE = 'MANUAL_ACTIVITY';
export const MANUAL_ACTIVITY_INFO_ROUTE = '/central-payment/edit/:id/info';

export const EMPTY_MANUAL_ACTIVITY: ManualActivity = {
    id: -1,
    provider: null,
    destination: null,
    visibility: "PUBLIC",
    last_author: null,
    modified_date: null,
    creator: null,
    created_date: null,
    poi_type: false,
    audio_guide: false,
    cover_picture: null,
    cut_line: null,
    description_lang: null,
    duration: 0,
    email: null,
    for_children: false,
    for_disabled: false,
    good_to_know: null,
    guide: false,
    guide_lang: null,
    included: null,
    is_custom: false,
    localization: [],
    long_description: null,
    max_pax: null,
    need_pax_weight: false,
    not_included: null,
    og_updated: false,
    original_product: null,
    owner: {
        id: 0,
        name: "",
        type: 0,
        logo: null,
        logo_minimal: null,
        phone_number: null,
        street: null,
        zip_code: null,
        city: null
    },
    phone_number: "",
    pictures: [],
    place: null,
    product_code: "",
    product_number_model: false,
    product_type: 0,
    provider_lib: null,
    provider_ref: null,
    rating: null,
    recommended: false,
    hide_localization: false,
    short_description: null,
    supplier_address: null,
    supplier_city_name: null,
    provider_name: null,
    supplier_phone: null,
    supplier_zip_code: null,
    title: '',
    voucher_text: null,
    website: "",
    contracts: null,
    multiple_days: false,
    special_attribute: null,
    selected_holidays: [],
    closed_days: [],
    variants: [],
    schedules: [],
    providers: [],
    linked_products: [],
    contacts: [],
    hotel_required: false,
    voucher_manual: false,
    label_eco: false,
    type: ActivityType.ACTIVITY,
    hide_hours: false,
    lock: false,
    publish_on_web: false,
    deactivated_contracts: [],
    is_pepite: false,
    fake_hotel_code: null
};

export const EMPTY_CANCELLATION_RULE: CancellationRule = {
    id: -1,
    product: 0,
    contract: undefined,
    created_date: null,
    creator: null,
    free_cancel: 0,
    last_author: null,
    localization: [],
    modified_date: null,
    name: null,
    rules: [],
    reference: "",
    periods: [{ start_date: null, end_date: null }]
};

export const EMPTY_CANCELLATION_CONDITION: Rule = {
    nb_days: 0,
    price_type: 'FIX',
    value: 0,
    cancellation_type: 0
};

export const CANCELLATION_RULE_LIST_FIELD: ManualActivityCancellationRuleListField[] = [
    {
        field_name: "name",
        label: "name",
        sortable: false,
        getValue: (cancellation_rule) => cancellation_rule.name || '',
    },
    {
        field_name: "reference",
        label: "reference",
        sortable: true,
        getValue: (cancellation_rule) => cancellation_rule.reference,
    },
    {
        field_name: "created_date",
        label: "created-date",
        sortable: true,
        getValue: (cancellation_rule) => cancellation_rule.created_date !== null ? format(new Date(cancellation_rule.created_date), 'P HH:mm', { locale: locales_cpy[i18next.language] }) : '',
    },
    {
        field_name: "creator",
        label: "created-by",
        sortable: true,
        getValue: (cancellation_rule) => cancellation_rule.creator?.username || '',
    },
    {
        field_name: "modified_date",
        label: "modified-date",
        sortable: true,
        getValue: (cancellation_rule) => cancellation_rule.modified_date !== null ? format(new Date(cancellation_rule.modified_date), 'P HH:mm', { locale: locales_cpy[i18next.language] }) : '',
    },
    {
        field_name: "last_author",
        label: "modified-by",
        sortable: true,
        getValue: (cancellation_rule) => cancellation_rule.last_author?.username || '',
    },
];

export const EMPTY_MANUAL_ACTIVITY_ERRORS: ManualActivityErrors = {
    name_error: ""
};


export const EMPTY_MANUAL_ACTIVITY_FILTERS: ManualActivityListFiltersCriteria = {
    providers: [],
    creator: "",
    last_author: "",
    created_date_gte: null,
    created_date_lte: null,
    modified_date_gte: null,
    modified_date_lte: null,
    period_type: 0,
    period: "MODIFICATION",
    status: "ALL",
    destination: null,
    country: null,
    name: "",
    type: 'undefined',
    is_pepite: "ALL"
};

export const EMPTY_MANUAL_ACTIVITY_LIST_CRITERIA: ManualActivityListCriteria = {
    limit: 50,
    page: 0,
    order_by: '',
    order: 'asc',
    filters: EMPTY_MANUAL_ACTIVITY_FILTERS,
    search: ''
};

export const DEFAULT_COLUMN: Column[] = [
    {
        label: 'type',
        field_name: 'POI_TYPE',
        checked: true,
        order_by: 'poi_type',
        fixed_width: true,
        last_updated: false
    },
    {
        label: 'reviewed-by-marketing',
        field_name: 'LOCK',
        checked: true,
        order_by: 'lock',
        fixed_width: true,
        last_updated: false
    },
    {
        label: 'website-publication',
        field_name: 'PUBLISHED_ON_WEB',
        checked: true,
        order_by: 'publish_on_web ',
        fixed_width: true,
        last_updated: false
    },
    {
        label: 'name',
        field_name: 'NAME',
        checked: true,
        order_by: 'title',
        fixed_width: false,
        last_updated: false
    }, {
        label: 'providers',
        field_name: 'PROVIDERS',
        checked: true,
        order_by: '',
        fixed_width: false,
        last_updated: false
    }, {
        label: 'destination',
        field_name: 'DESTINATION',
        checked: true,
        order_by: 'destination__current_version__localization__name',
        fixed_width: false,
        last_updated: false
    }, {
        label: 'country',
        field_name: 'COUNTRY',
        checked: true,
        order_by: 'destination__current_version__country__international_name',
        fixed_width: false,
        last_updated: false
    }, {
        label: 'pepite',
        field_name: 'PEPITE',
        checked: true,
        order_by: 'is_pepite',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'online-to',
        field_name: 'VISIBILITY',
        checked: true,
        order_by: '',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'online-agency',
        field_name: 'AGENCY_VISIBILITY',
        checked: true,
        order_by: '',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'created-by',
        field_name: 'CREATED_BY',
        checked: false,
        order_by: 'creator__username',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'modified-date',
        field_name: 'MODIFIED_DATE',
        checked: true,
        order_by: 'modified_date',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'modified-by',
        field_name: 'MODIFIED_BY',
        checked: false,
        order_by: 'last_author__username',
        fixed_width: true,
        last_updated: false
    }];

export const EMPTY_MANUAL_ACTIVITY_LIST_CONFIGURATION: ManualActivityListConfiguration = {
    columns: DEFAULT_COLUMN,
    preferences: []
};


export const EMPTY_MANUAL_ACTIVITY_CONTACT: ManualActivityContact = {
    id: -1,
    created_date: new Date(),
    modified_date: new Date,
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    creator: null,
    last_author: null,
    parent: undefined,
    is_detailed: undefined,
};

export const CONTACT_LIST_FIELD: ManualActivityContactListField[] = [
    {
        field_name: "name",
        label: "name",
        sortable: true,
        getValue: (contact) => `${contact.first_name} ${contact.last_name}`,
    },
    {
        field_name: "contact_email",
        label: "email",
        sortable: true,
        getValue: (contact) => contact.email,
    },
    {
        field_name: "phone_number",
        label: "phone-number",
        sortable: false,
        getValue: (contact) => contact.phone_number,
    }
];

export const EMPTY_CLOSE_DAY: Omit<ClosedDay, 'id' | 'creator' | 'created_date' | 'modified_date' | 'last_author' | 'product'> = {
    day: format(new Date(), 'yyyy-MM-dd'),
    repeating: false
};

export const EMPTY_OPEN_DAY: OpenDays = {
    id: 0,
    days: [],
    description: "",
    edit: false,
    hours: [["00:00:00", "00:00:00"]],
    type: "FIXED",
    periods: [],
    months: []
};

export const EMPTY_CONTRACT_PARAMETERS: ManualActivityContractParameters = {
    pricing_type: PricingType.PURCHASE,
    com_is_percentage: false,
    global_com: "",
    com_calculation_as_percentage: false,
    markup_is_percentage: false,
    global_markup: "",
    com_applied_on_ttc: false
};

export const EMPTY_MANUAL_ACTIVITY_PRICE: ManualActivityPrice = {
    id: 0,
    contract: 0,
    product: 0,
    hours_selected: [],
    start_date: "",
    end_date: "",
    creator: 0,
    last_author: 0,
    model: null,
    variant: 0,
    schedule: null,
    values: [],
    stock: [],
    purchase_price: 0,
    selling_price: 0,
    com_price: 0,
    markup_price: 0
};

export const EMPTY_MANUAL_ACTIVITY_PRICE_UTIL: ManualActivityPriceUtil = {
    model: null,
    schedule: null,
    purchase_price: "",
    purchase_price_error: "",
    selling_price: "",
    selling_price_error: "",
    com_price: "",
    markup_price: "",
    hours_selected: [],
    values: [],
    variant: null
};

export const EMPTY_PLACE: Place = {
    address: "",
    city_name: "",
    zip_code: "",
    latitude: "",
    longitude: "",
    google_id: "",
    zoom: 4,
    country_name: null,
    displayed_destination: '',
    id: 0
};


export const EMPTY_MANUAL_ACTIVITY_LOCALIZED: ManualActivityLocalized = {
    title: '',
    short_description: null,
    locale: 0,
    long_description: null,
    good_to_know: '',
    voucher_text: '',
    guide_lang: [],
    not_recommended: [],
    included: [],
    not_included: [],
    prohibited_items: [],
    information_required_at_booking: [],
    open_field_meeting_point: null,
    open_field_return_point: null
};